// redux/slices/apiSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL;

const apiEndpoints = {
  mainContent: `${apiUrl}/api/main_content`,
  extraHeal: `${apiUrl}/api/extra_heal`,
  about: `${apiUrl}/api/about`,
  qa: `${apiUrl}/api/qa`,
  contact: `${apiUrl}/api/contact`,
  product: `${apiUrl}/api/product`,
};

// Generic thunk for GET
export const fetchData = createAsyncThunk(
  "api/fetchData",
  async ({ model }, { rejectWithValue }) => {
    try {
      const response = await axios.get(apiEndpoints[model]);
      return { model, data: response.data };
    } catch (error) {
      return rejectWithValue({ model, error: error.message });
    }
  }
);

// Generic thunk for POST
export const createData = createAsyncThunk(
  "api/createData",
  async ({ model, newData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(apiEndpoints[model], newData);
      return { model, data: response.data };
    } catch (error) {
      return rejectWithValue({ model, error: error.message });
    }
  }
);

// Generic thunk for PUT
export const updateData = createAsyncThunk(
  "api/updateData",
  async ({ model, id, updatedData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${apiEndpoints[model]}${id}/`,
        updatedData
      );
      return { model, data: response.data };
    } catch (error) {
      return rejectWithValue({ model, error: error.message });
    }
  }
);

// Generic thunk for DELETE
export const deleteData = createAsyncThunk(
  "api/deleteData",
  async ({ model, id }, { rejectWithValue }) => {
    try {
      await axios.delete(`${apiEndpoints[model]}${id}/`);
      return { model, id };
    } catch (error) {
      return rejectWithValue({ model, error: error.message });
    }
  }
);

const initialStateTemplate = {
  data: [],
  status: "idle",
  error: null,
};

// Slice
const apiSlice = createSlice({
  name: "api",
  initialState: {
    mainContent: { ...initialStateTemplate },
    extraHeal: { ...initialStateTemplate },
    about: { ...initialStateTemplate },
    qa: { ...initialStateTemplate },
    contact: { ...initialStateTemplate },
    product: { ...initialStateTemplate },
  },
  reducers: {},
  extraReducers: (builder) => {
    // Handle fetchData
    builder
      .addCase(fetchData.pending, (state, action) => {
        const model = action.meta.arg.model;
        state[model].status = "loading";
      })
      .addCase(fetchData.fulfilled, (state, action) => {
        const { model, data } = action.payload;
        state[model].status = "succeeded";
        state[model].data = data;
        state[model].error = null;
      })
      .addCase(fetchData.rejected, (state, action) => {
        const { model, error } = action.payload;
        state[model].status = "failed";
        state[model].error = error;
      });

    // Handle createData
    builder
      .addCase(createData.pending, (state, action) => {
        const model = action.meta.arg.model;
        state[model].status = "loading";
      })
      .addCase(createData.fulfilled, (state, action) => {
        const { model, data } = action.payload;
        state[model].status = "succeeded";
        state[model].data.push(data);
        state[model].error = null;
      })
      .addCase(createData.rejected, (state, action) => {
        const { model, error } = action.payload;
        state[model].status = "failed";
        state[model].error = error;
      });

    // Handle updateData
    builder
      .addCase(updateData.pending, (state, action) => {
        const model = action.meta.arg.model;
        state[model].status = "loading";
      })
      .addCase(updateData.fulfilled, (state, action) => {
        const { model, data } = action.payload;
        state[model].status = "succeeded";
        const index = state[model].data.findIndex(
          (item) => item.id === data.id
        );
        if (index !== -1) state[model].data[index] = data;
        state[model].error = null;
      })
      .addCase(updateData.rejected, (state, action) => {
        const { model, error } = action.payload;
        state[model].status = "failed";
        state[model].error = error;
      });

    // Handle deleteData
    builder
      .addCase(deleteData.pending, (state, action) => {
        const model = action.meta.arg.model;
        state[model].status = "loading";
      })
      .addCase(deleteData.fulfilled, (state, action) => {
        const { model, id } = action.payload;
        state[model].status = "succeeded";
        state[model].data = state[model].data.filter((item) => item.id !== id);
        state[model].error = null;
      })
      .addCase(deleteData.rejected, (state, action) => {
        const { model, error } = action.payload;
        state[model].status = "failed";
        state[model].error = error;
      });
  },
});

export default apiSlice.reducer;
